// ─── Import d'un relevé bancaire (OFX / CSV) ──────────────────────
// WEB : <input file> caché. NATIF : expo-document-picker + expo-file-system,
// lus en OCTETS BRUTS puis décodés selon le format (accents des relevés).
// Les deux modules sont chargés en `require` PARESSEUX : jamais côté web, et
// aucun crash si le module manque.
//
// Rien n'est automatique : chaque ligne porte une action que l'utilisateur peut
// changer — Rapprocher (l'op existe déjà, on l'enrichit) · Importer (créer) ·
// Ignorer. La suggestion n'est qu'un premier filtre.
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { View, Text, TouchableOpacity, ScrollView, StyleSheet, Platform, ActivityIndicator } from 'react-native';
import { Feuille, ChoixPuces, stylesForm, useUI, useT } from '@mop/core';
import { formatEuro, montantOperation } from './services/calculs';
import { decoderReleve, detecterFormat, parserReleve, compteDuReleve, soldeDuReleve } from './services/releveBancaire';
import { compteParAcctid, associerAcctid, emojiCompte } from './services/comptes';
import { verifierCoherence } from './services/coherenceImport';
import { analyserOperations, appliquerImport } from './services/import';

// Natif : octets bruts → décodage maison (Hermes lit en UTF-8, les relevés sont
// souvent en windows-1252). Repli legacy si l'API « File » n'existe pas.
async function lireFichierNatif(uri, format) {
  const FS = require('expo-file-system');
  if (FS?.File) {
    const f = new FS.File(uri);
    if (typeof f.bytes === 'function') return decoderReleve(await f.bytes(), format);
    if (typeof f.text === 'function') return await f.text();
  }
  return await require('expo-file-system/legacy').readAsStringAsync(uri);
}

// Libellés déjà traduits dans le Core (FR/EN) — on ne réinvente pas de clés.
const ACTIONS = [
  { id: 'rapprocher', cle: 'budget.importActionRapprocher' },
  // Rattacher : viser une echeance PREVUE du mois (le loyer attendu), la ou
  // « rapprocher » vise une operation deja saisie.
  { id: 'rattacher',  cle: 'budget.importActionRattacher' },
  { id: 'importer',   cle: 'budget.importActionImporter' },
  { id: 'ignorer',    cle: 'budget.importActionIgnorer' },
];

export default function FeuilleImport({ visible, espaceId, user, comptes = [], onFermer, onImporte }) {
  const UI = useUI();
  const t = useT();
  const s = useMemo(() => makeStyles(UI), [UI]);
  const [compteId, setCompteId] = useState(null);
  const [acctid, setAcctid] = useState('');       // identifiant lu dans le relevé OFX
  // Le solde OFFICIEL annoncé par la banque (LEDGERBAL) : affiché à la
  // confirmation, et remonté à l'écran pour le rapprochement (16/08).
  const [soldeBanque, setSoldeBanque] = useState(null);
  const [nomFichier, setNomFichier] = useState('');
  const [brutes, setBrutes] = useState([]);       // opérations lues, pour la vérification
  const [aConfirmer, setAConfirmer] = useState(false);
  const [ops, setOps] = useState(null);
  const [actions, setActions] = useState({});
  const [occupe, setOccupe] = useState(false);
  const [erreur, setErreur] = useState('');
  const [resultat, setResultat] = useState(null);
  const inputRef = useRef(null);
  const reqRef = useRef(0);   // invalide une analyse encore en vol si on réinitialise
  const texteRef = useRef('');   // contenu du dernier fichier lu, pour ré-analyser

  useEffect(() => {
    reqRef.current++;
    if (visible) {
      setNomFichier(''); setOps(null); setActions({}); setErreur(''); setResultat(null); setOccupe(false);
      setAcctid(''); setBrutes([]); setAConfirmer(false); setSoldeBanque(null);
      // Un seul compte : pas de question à poser.
      setCompteId(comptes.length === 1 ? comptes[0].id : null);
    }
  }, [visible, comptes]);

  // Suggestion : déjà importé → ignorer ; doublon NON AMBIGU → rapprocher ;
  // sinon importer. Jamais de rapprochement suggéré si plusieurs candidats.
  const actionParDefaut = (o) => {
    if (o.statut === 'deja') return 'ignorer';
    if (o.statut === 'possible' && o.match?.id && (o.matchCount || 1) === 1) return 'rapprocher';
    // Ligne prevue reperee : proposee par defaut, mais elle reste modifiable —
    // c'est une SUGGESTION, comme le reste de cet ecran.
    if (o.matchLigne?.ligneId) return 'rattacher';
    return 'importer';
  };

  const analyser = async (nom, texte, compteForce) => {
    const jeton = ++reqRef.current;
    texteRef.current = texte;
    setOccupe(true); setErreur('');
    try {
      // Un relevé OFX porte le numéro de compte : on reconnaît le compte si on
      // l'a déjà vu, sinon on mémorisera le lien après l'import.
      const info = compteDuReleve(texte);
      setSoldeBanque(soldeDuReleve(texte));
      // Le compte reconnu doit être connu AVANT l'analyse : c'est lui qui borne
      // la détection des doublons (une même opération sur deux comptes n'est pas
      // un doublon).
      let cible = compteForce !== undefined ? compteForce : compteId;
      if (info?.acctid) {
        setAcctid(info.acctid);
        const connu = compteParAcctid(comptes, info.acctid);
        // Le compte reconnu dans le fichier prime, sauf si l'utilisateur vient
        // justement d'en choisir un autre à la main.
        if (connu && compteForce === undefined) { cible = connu.id; setCompteId(connu.id); }
      }
      const brutes = parserReleve(nom, texte);
      if (!brutes.length) { setErreur(t('budget.importAucune')); setOccupe(false); return; }
      const analysees = await analyserOperations({ espaceId, operations: brutes, compteId: cible });
      if (jeton !== reqRef.current) return;               // analyse périmée
      const parDefaut = {};
      analysees.forEach(o => { parDefaut[o.ref] = actionParDefaut(o); });
      setOps(analysees); setActions(parDefaut);
      // Rien n'est écrit tant que l'utilisateur n'a pas confirmé que ce relevé
      // est bien celui du compte affiché : un import mal aiguillé fausse DEUX
      // soldes et ne se remarque que bien plus tard.
      setBrutes(brutes);
      setAConfirmer(true);
    } catch (e) {
      if (jeton === reqRef.current) setErreur(e?.message || 'Erreur');
    } finally {
      if (jeton === reqRef.current) setOccupe(false);
    }
  };

  // Changer de compte APRÈS le chargement invalide l'analyse : les doublons
  // sont calculés PAR COMPTE. On refait donc l'analyse à partir du fichier déjà
  // lu, plutôt que d'afficher un résultat qui ne correspond plus au compte.
  // (Défini après `analyser`, qu'il appelle — règle du projet sur les const.)
  const surChangementCompte = (id) => {
    setCompteId(id);
    if (texteRef.current && nomFichier) analyser(nomFichier, texteRef.current, id);
  };

  const choisirFichier = async () => {
    setErreur('');
    if (Platform.OS === 'web') { inputRef.current?.click(); return; }
    try {
      const DocumentPicker = require('expo-document-picker');
      const r = await DocumentPicker.getDocumentAsync({ type: '*/*', copyToCacheDirectory: true });
      const f = r?.assets?.[0];
      if (!f?.uri) return;
      setNomFichier(f.name || '');
      const texte = await lireFichierNatif(f.uri, detecterFormat(f.name, ''));
      await analyser(f.name || '', texte);
    } catch (e) { setErreur(e?.message || 'Erreur'); }
  };

  const surFichierWeb = async (ev) => {
    const f = ev?.target?.files?.[0];
    if (!f) return;
    setNomFichier(f.name || '');
    const buf = await f.arrayBuffer();
    await analyser(f.name || '', decoderReleve(new Uint8Array(buf), detecterFormat(f.name, '')));
    ev.target.value = '';   // permet de re-choisir le même fichier
  };

  const importer = async () => {
    setOccupe(true); setErreur('');
    try {
      const rapprochements = [], rattachements = [], creations = [];
      for (const o of (ops || [])) {
        const a = actions[o.ref];
        if (a === 'rapprocher' && o.match?.id) {
          rapprochements.push({ op: o, depenseId: o.match.id, budgetId: o.match.budgetId, categorie: o.match.categorie, recurrenceId: o.match.recurrenceId });
        } else if (a === 'rattacher' && o.matchLigne?.ligneId) {
          rattachements.push({
            op: o, ligneId: o.matchLigne.ligneId, budgetId: o.matchLigne.budgetId,
            categorie: o.matchLigne.categorie, mois: o.matchLigne.mois,
          });
        } else if (a === 'importer') {
          creations.push({ op: o, budgetId: o.regle?.budgetId || null, categorie: o.regle?.categorie || 'import', recurrenceId: o.regle?.recurrenceId || null });
        }
      }
      const r = await appliquerImport({ espaceId, user, compteId, rapprochements, rattachements, creations });
      // Mémorise « ce numéro de relevé = ce compte » pour les imports suivants.
      if (compteId && acctid) await associerAcctid(compteId, acctid).catch(() => {});
      setResultat(r);
      // Le compte et le solde de la banque accompagnent le bilan : c'est
      // l'écran, qui a TOUTES les opérations, qui fait le rapprochement.
      onImporte?.({ ...r, compteId, soldeBanque });
    } catch (e) { setErreur(e?.message || 'Erreur'); }
    finally { setOccupe(false); }
  };

  const aTraiter = (ops || []).filter(o => actions[o.ref] !== 'ignorer').length;
  const compte = comptes.find(c => c.id === compteId) || null;
  // Les opérations DÉJÀ en base servent à repérer un relevé qui aurait déjà été
  // importé sur un autre compte.
  const existantes = useMemo(
    () => (ops || []).map(o => o.match).filter(Boolean),
    [ops],
  );
  const verif = useMemo(
    () => verifierCoherence({ compte, acctid, nomFichier, operations: brutes, existantes }),
    [compte, acctid, nomFichier, brutes, existantes],
  );

  return (
    <Feuille visible={visible} titre={t('budget.importTitre')} emoji="📥" hauteur={0.85} onFermer={onFermer}
      action={ops && !resultat && !aConfirmer
        ? { icone: '📥', label: t('budget.importer'), disabled: occupe || !aTraiter, onPress: importer }
        : undefined}>

      {Platform.OS === 'web' && (
        <input ref={inputRef} type="file" accept=".ofx,.qfx,.csv,text/csv" style={{ display: 'none' }} onChange={surFichierWeb} />
      )}

      {/* Le COMPTE d'où vient le relevé : sans lui, impossible de suivre un
          solde ni de repérer les virements entre ses propres comptes. */}
      {comptes.length > 1 && (
        <>
          <Text style={s.lbl}>{t('finances.compteDuReleve')}</Text>
          <ChoixPuces options={comptes.map(c => ({ id: c.id, label: `${emojiCompte(c)} ${c.nom}` }))}
            value={compteId} onChange={surChangementCompte} />
        </>
      )}

      <TouchableOpacity style={s.choisir} activeOpacity={0.7} onPress={choisirFichier} disabled={occupe}>
        <Text style={{ fontSize: 18 }}>📄</Text>
        <Text style={s.choisirTxt} numberOfLines={1}>{nomFichier || t('budget.importChoisir')}</Text>
      </TouchableOpacity>
      <Text style={s.aide}>{t('budget.importNote')}</Text>
      {!!acctid && !compteId && <Text style={s.aide}>⚠️ {t('finances.compteInconnu')}</Text>}

      {occupe && <ActivityIndicator style={{ marginTop: 16 }} color={UI.accent} />}
      {!!erreur && <Text style={s.erreur}>{erreur}</Text>}

      {/* CONFIRMATION : on récapitule le compte de destination et le contenu du
          relevé, et on signale toute incohérence AVANT d'écrire quoi que ce
          soit. Un blocage ne peut pas être forcé. */}
      {aConfirmer && !resultat && (
        <View style={[s.confirm, verif.niveau === 'blocage' && s.confirmBloc,
          verif.niveau === 'attention' && s.confirmAtt]}>
          <Text style={s.confirmTitre}>
            {verif.niveau === 'blocage' ? '⛔' : verif.niveau === 'attention' ? '⚠️' : '❓'}{' '}
            {t('finances.confirmerCompte', { compte: compte ? compte.nom : '—' })}
          </Text>
          {!!compte && (
            <Text style={s.confirmLigne}>
              {emojiCompte(compte)} {compte.nom}
              {compte.banque ? `  ·  ${compte.banque}` : ''}
              {compte.fin4 ? `  ·  ••${compte.fin4}` : ''}
            </Text>
          )}
          <Text style={s.confirmLigne}>
            📄 {nomFichier || '—'}
          </Text>
          <Text style={s.confirmLigne}>
            {t('finances.releveResume', {
              n: verif.resume.nb, du: verif.resume.du, au: verif.resume.au,
              debits: formatEuro(verif.resume.debits), credits: formatEuro(verif.resume.credits),
            })}
          </Text>
          {/* Le solde OFFICIEL de la banque, quand le relevé le porte : c'est
              le chiffre auquel l'app se comparera après l'import. */}
          {!!soldeBanque && (
            <Text style={s.confirmLigne}>
              🏦 {t('finances.soldeBanqueLigne', {
                solde: formatEuro(soldeBanque.montant), date: soldeBanque.date,
              })}
            </Text>
          )}
          {verif.messages.map((m, i) => (
            <Text key={i} style={[s.confirmMsg, m.niveau === 'blocage' && { color: UI.danger || '#c0392b' }]}>
              • {t(m.cle, m.params || {})}
            </Text>
          ))}
          <View style={s.confirmActions}>
            <TouchableOpacity style={s.btnAnnuler} activeOpacity={0.7}
              onPress={() => { setOps(null); setAConfirmer(false); setNomFichier(''); setBrutes([]); }}>
              <Text style={s.btnAnnulerTxt}>{t('commun.annuler')}</Text>
            </TouchableOpacity>
            <TouchableOpacity style={[s.btnOk, verif.niveau === 'blocage' && { opacity: 0.35 }]}
              disabled={verif.niveau === 'blocage'} activeOpacity={0.7}
              onPress={() => setAConfirmer(false)}>
              <Text style={s.btnOkTxt}>{t('finances.ouiCEstBien')}</Text>
            </TouchableOpacity>
          </View>
        </View>
      )}

      {resultat ? (
        <View style={s.bilan}>
          {/* Le bilan ne mentionne les rattachements que s'il y en a : sur un
              relevé sans échéance prévue, la ligne resterait à zéro pour rien. */}
          <Text style={s.bilanTxt}>✓ {resultat.rattaches
            ? t('budget.importResultat3', { rapproches: resultat.rapproches, rattaches: resultat.rattaches, crees: resultat.crees })
            : t('budget.importResultat2', { rapproches: resultat.rapproches, crees: resultat.crees })}</Text>
        </View>
      ) : aConfirmer ? null : (ops || []).map(o => {
        const a = actions[o.ref];
        return (
          <View key={o.ref} style={[s.ligne, a === 'ignorer' && { opacity: 0.5 }]}>
            <View style={s.tete}>
              <Text style={s.libelle} numberOfLines={1}>{o.libelle}</Text>
              <Text style={[s.montant, o.montant >= 0 && { color: UI.ok || '#2e7d32' }]}>
                {montantOperation(o.montant, o.montant >= 0 ? 'revenu' : 'depense')}
              </Text>
            </View>
            <Text style={s.sous} numberOfLines={1}>
              {o.date}
              {o.statut === 'deja' ? `  ·  ${t('budget.importDoublonDeja')}` : ''}
              {o.match ? `  ·  ↔ ${o.match.libelle || o.match.budget}` : ''}
              {(!o.match && o.matchLigne) ? `  ·  ↔ ${o.matchLigne.libelle} (${t('budget.importPrevu')} ${montantOperation(o.matchLigne.prevu, 'depense')})` : ''}
              {o.regle ? `  ·  → ${o.regle.budgetNom}` : ''}
            </Text>
            <View style={s.actions}>
              {ACTIONS.map(x => {
                const off = (x.id === 'rapprocher' && !o.match?.id)
                  || (x.id === 'rattacher' && !o.matchLigne?.ligneId);
                return (
                  <TouchableOpacity key={x.id} disabled={off}
                    style={[s.chip, a === x.id && s.chipOn, off && { opacity: 0.3 }]}
                    onPress={() => setActions(p => ({ ...p, [o.ref]: x.id }))}>
                    <Text style={[s.chipTxt, a === x.id && s.chipTxtOn]}>{t(x.cle)}</Text>
                  </TouchableOpacity>
                );
              })}
            </View>
          </View>
        );
      })}
    </Feuille>
  );
}

const makeStyles = (UI) => StyleSheet.create({
  ...stylesForm(UI),
  choisir: { flexDirection: 'row', alignItems: 'center', gap: 11, backgroundColor: UI.card, borderRadius: 12, borderWidth: 0.5, borderColor: UI.border, padding: 13 },
  choisirTxt: { flex: 1, fontSize: 14.5, color: UI.ink, fontWeight: '600' },
  aide: { fontSize: 12, color: UI.faint, lineHeight: 17, marginTop: 6 },
  erreur: { fontSize: 13, color: UI.danger || '#c0392b', marginTop: 10 },
  ligne: { marginTop: 10, padding: 11, backgroundColor: UI.card, borderRadius: 12, borderWidth: 0.5, borderColor: UI.border },
  tete: { flexDirection: 'row', alignItems: 'center', gap: 10 },
  libelle: { flex: 1, fontSize: 14, fontWeight: '700', color: UI.ink },
  montant: { fontSize: 14.5, fontWeight: '800', color: UI.ink },
  sous: { fontSize: 11.5, color: UI.muted, marginTop: 2 },
  actions: { flexDirection: 'row', gap: 7, marginTop: 9 },
  chip: { paddingVertical: 6, paddingHorizontal: 11, borderRadius: 999, borderWidth: 0.5, borderColor: UI.border, backgroundColor: UI.bg },
  chipOn: { backgroundColor: UI.accent, borderColor: UI.accent },
  chipTxt: { fontSize: 12, color: UI.muted, fontWeight: '700' },
  chipTxtOn: { color: '#fff' },
  bilan: { marginTop: 14, padding: 13, borderRadius: 12, backgroundColor: UI.card, borderWidth: 0.5, borderColor: UI.border },
  bilanTxt: { fontSize: 14, color: UI.ink, fontWeight: '700' },
  confirm: { marginTop: 14, padding: 14, borderRadius: 13, backgroundColor: UI.card, borderWidth: 1, borderColor: UI.accent },
  confirmAtt: { borderColor: '#e8a33d' },
  confirmBloc: { borderColor: UI.danger || '#c0392b' },
  confirmTitre: { fontSize: 15, fontWeight: '800', color: UI.ink, marginBottom: 8 },
  confirmLigne: { fontSize: 13.5, color: UI.muted, marginTop: 3 },
  confirmMsg: { fontSize: 13, color: '#b8791f', marginTop: 7, lineHeight: 18 },
  confirmActions: { flexDirection: 'row', gap: 10, marginTop: 14 },
  btnAnnuler: { flex: 1, paddingVertical: 12, borderRadius: 11, borderWidth: 0.5, borderColor: UI.border, alignItems: 'center' },
  btnAnnulerTxt: { fontSize: 14, color: UI.muted, fontWeight: '700' },
  btnOk: { flex: 2, paddingVertical: 12, borderRadius: 11, backgroundColor: UI.accent, alignItems: 'center' },
  btnOkTxt: { fontSize: 14, color: '#fff', fontWeight: '800' },
});
